import { useClientSideTableFilter } from "~components/DataTable/hooks/useClientSideTableFilter.ts";
import type { FixtureTransformed } from "~pages/Fixtures/types";
import { FixturesFilterTypes } from "../types";

type Competitor = {
  entityId?: string | null;
  entity?: { id?: string | null };
  isHome?: boolean;
};

type Participant = {
  personId?: string | null;
  person?: { id?: string | null };
  isHome?: boolean;
};

type DateRangeValue = {
  format: (format: string) => string;
};

type DateRangeFilterValue = {
  from?: DateRangeValue | null;
  to?: DateRangeValue | null;
};

type FixtureFilterRecord = FixtureTransformed & {
  competitors?: Competitor[];
  participants?: Participant[];
  venueId?: string | null;
  stageCode?: string | null;
  poolCode?: string | null;
  seriesCode?: string | null;
  startTimeLocal?: string | null;
};

type SelectedFilterValues = unknown[];

const getCompetitorId = (competitor: Competitor) => competitor.entityId ?? competitor.entity?.id;

const getParticipantId = (participant: Participant) => participant.personId ?? participant.person?.id;

const includesEmptyValue = (selectedValues: SelectedFilterValues) => selectedValues.includes("");

const isDateRangeFilterValue = (filterValue: unknown): filterValue is DateRangeFilterValue => {
  return typeof filterValue === "object" && filterValue !== null;
};

const fixtureFilterFunctions = {
  [FixturesFilterTypes.Competitors]: (fixture: FixtureFilterRecord, selectedValues: SelectedFilterValues) => {
    const competitors = fixture.competitors ?? [];

    return (
      competitors.some((competitor) => selectedValues.includes(getCompetitorId(competitor))) ||
      (includesEmptyValue(selectedValues) && competitors.length === 0)
    );
  },
  [FixturesFilterTypes.Participants]: (fixture: FixtureFilterRecord, selectedValues: SelectedFilterValues) => {
    const participants = fixture.participants ?? [];

    return (
      participants.some((participant) => selectedValues.includes(getParticipantId(participant))) ||
      (includesEmptyValue(selectedValues) && participants.length === 0)
    );
  },
  [FixturesFilterTypes.RoundNumber]: (fixture: FixtureFilterRecord, selectedValues: SelectedFilterValues) =>
    selectedValues.includes(fixture.roundNumber ?? ""),
  [FixturesFilterTypes.Venues]: (fixture: FixtureFilterRecord, selectedValues: SelectedFilterValues) =>
    selectedValues.includes(fixture.venueId ?? ""),
  [FixturesFilterTypes.DateRange]: (fixture: FixtureFilterRecord, selectedValues: SelectedFilterValues) => {
    const dateRange = selectedValues[0];

    if (!isDateRangeFilterValue(dateRange)) {
      return true;
    }

    const fixtureDate = fixture.startTimeLocal?.split("T")[0];
    if (!fixtureDate) {
      return false;
    }

    const isAfterOrOnStart = !dateRange.from || fixtureDate >= dateRange.from.format("YYYY-MM-DD");
    const isBeforeOrOnEnd = !dateRange.to || fixtureDate <= dateRange.to.format("YYYY-MM-DD");

    return isAfterOrOnStart && isBeforeOrOnEnd;
  },
  [FixturesFilterTypes.FixtureTypes]: (fixture: FixtureFilterRecord, selectedValues: SelectedFilterValues) =>
    selectedValues.includes(fixture.fixtureType ?? ""),
  [FixturesFilterTypes.FixtureStatuses]: (fixture: FixtureFilterRecord, selectedValues: SelectedFilterValues) =>
    selectedValues.includes(fixture.status ?? ""),
  [FixturesFilterTypes.Stages]: (fixture: FixtureFilterRecord, selectedValues: SelectedFilterValues) =>
    selectedValues.includes(fixture.stageCode ?? ""),
  [FixturesFilterTypes.Pools]: (fixture: FixtureFilterRecord, selectedValues: SelectedFilterValues) =>
    selectedValues.includes(fixture.poolCode ?? ""),
  [FixturesFilterTypes.Series]: (fixture: FixtureFilterRecord, selectedValues: SelectedFilterValues) =>
    selectedValues.includes(fixture.seriesCode ?? ""),
};

export const useFilterFixtures = (
  serverFixtures: FixtureFilterRecord[] = [],
  currentFilter: Record<string, unknown>,
): FixtureTransformed[] => {
  return useClientSideTableFilter({
    data: serverFixtures,
    currentFilter,
    filterFunctions: fixtureFilterFunctions,
  }).filteredData;
};
